import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  Briefcase,
  Landmark,
  ShieldCheck,
  Award
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

const AssignmentsHome = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const res = await axios.get(
        `${API_URL}/api/assignments/dashboard`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setStats(res.data);
    } catch (err) {
      console.error("Dashboard load error:", err);
    } finally {
      setLoading(false);
    }
  };

  // 🎨 Category Style Mapping
  const getCategoryStyle = (name) => {
    const lower = name?.toLowerCase();

    if (lower?.includes("advisory"))
      return {
        icon: <Briefcase size={20} />,
        bg: "bg-white",
        iconBg: "bg-blue-100 text-blue-600",
        number: "text-blue-600"
      };

    if (lower?.includes("audit"))
      return {
        icon: <Landmark size={20} />,
        bg: "bg-white",
        iconBg: "bg-purple-100 text-purple-600",
        number: "text-purple-600"
      };

    if (lower?.includes("compliance"))
      return {
        icon: <ShieldCheck size={20} />,
        bg: "bg-white",
        iconBg: "bg-emerald-100 text-emerald-600",
        number: "text-emerald-600"
      };

    if (lower?.includes("certification"))
      return {
        icon: <Award size={20} />,
        bg: "bg-white",
        iconBg: "bg-amber-100 text-amber-600",
        number: "text-amber-600"
      };

    // default fallback
    return {
      icon: <Briefcase size={20} />,
      bg: "bg-slate-50",
      iconBg: "bg-slate-200 text-slate-600",
      number: "text-slate-700"
    };
  };

  return (
    <div>
      {/* HEADER */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-slate-800">
          Assignments Dashboard
        </h1>

        <button
          onClick={() => navigate("/assignments/register")}
          className="
            inline-flex items-center gap-2
            bg-gradient-to-r from-indigo-600 to-indigo-700
            hover:from-indigo-700 hover:to-indigo-800
            text-white px-5 py-2.5 rounded-xl
            text-sm font-semibold
            shadow-md hover:shadow-lg
            transition-all duration-200
          "
        >
          View All
          <ArrowRight size={16} />
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500">Loading dashboard...</div>
      ) : (
        <div className="grid md:grid-cols-3 gap-6">
          {stats.map((item) => {
            const style = getCategoryStyle(item.CategoryName);

            return (
              <div
                key={item.CategoryID}
                onClick={() =>
                  navigate(
                    `/assignments/register?categoryId=${item.CategoryID}&categoryName=${encodeURIComponent(item.CategoryName)}`
                  )
                }
                className={`
                  ${style.bg}
                  rounded-2xl border
                  p-6 shadow-sm
                  hover:shadow-xl hover:-translate-y-1
                  transition-all duration-300
                  cursor-pointer
                `}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className={`${style.iconBg} p-3 rounded-xl`}>
                      {style.icon}
                    </div>

                    <h3 className="text-lg font-semibold text-slate-800">
                      {item.CategoryName}
                    </h3>
                  </div>

                  <ArrowRight size={18} className="text-slate-400" />
                </div>

                {/* Stat */}
                <div>
                  <p className="text-sm text-slate-500">
                    Active Engagements
                  </p>

                  <p className={`text-3xl font-bold mt-1 ${style.number}`}>
                    {item.ActiveEngagements}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AssignmentsHome;
